/**
 * RevokeTrainingButton — Batch 18-E17
 *
 * Manager-facing revoke action for already-verified training sessions.
 * Parallel to VerifyTrainingButton (18-E12). All authority and
 * atomicity guarantees live in the 18-E15 revoke_training_completion
 * SECURITY DEFINER RPC behind the verify-training-completion Edge
 * Function — this component performs no direct DB writes.
 *
 * Display gating:
 *   - Hidden when the managerTrainingVerification feature flag is off.
 *   - Hidden when the session is not currently verified
 *     (managerVerifiedAt is null/undefined).
 *
 * Confirmation dialog requires a non-empty note. The Revoke button
 * inside the dialog is disabled until note.trim() is non-empty —
 * the RPC also enforces this server-side via the
 * REVOCATION_NOTE_REQUIRED check.
 *
 * This component does NOT:
 *   - write to training_consumption_sessions directly
 *   - write to training_completion_verifications directly
 *   - emit training_effectiveness_events
 *   - reference 'manager_verified' / 'revoked' as event-type literals
 *   - know about session_token / extension_evidence / Companion code
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ShieldOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useRevokeTrainingCompletion } from '@/hooks/useRevokeTrainingCompletion';

interface RevokeTrainingButtonProps {
  sessionId: string;
  managerVerifiedAt: string | null;
  completionStatus?: string | null;
  artifactTitle?: string | null;
  disabled?: boolean;
  onRevoked?: () => void;
}

// Friendly mapping for the Edge Function's generic error strings.
function describeError(message: string | undefined): string {
  const msg = (message ?? '').toLowerCase();
  if (msg.includes('not verified')) {
    return 'This training is not currently verified.';
  }
  if (msg.includes('self-revocation')) {
    return 'You cannot revoke your own training verification.';
  }
  if (msg.includes('not authorized')) {
    return 'You do not have permission to revoke this training verification.';
  }
  if (msg.includes('revocation note is required')) {
    return 'A note is required to revoke verification.';
  }
  if (msg.includes('session not found')) {
    return 'Training session no longer exists.';
  }
  return 'Revoking verification failed. Please try again.';
}

export function RevokeTrainingButton({
  sessionId,
  managerVerifiedAt,
  artifactTitle,
  disabled = false,
  onRevoked,
}: RevokeTrainingButtonProps) {
  const flagEnabled = useFeatureFlag('managerTrainingVerification');
  const revoke = useRevokeTrainingCompletion();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  // Display-only gating. The Edge Function + RPC remain the
  // authoritative reject points.
  if (!flagEnabled) return null;
  if (!managerVerifiedAt) return null;

  const trimmedNote = note.trim();
  const canSubmit = trimmedNote.length > 0 && !revoke.isPending;

  const handleRevoke = async () => {
    if (!trimmedNote) return;
    try {
      await revoke.mutateAsync({ sessionId, note: trimmedNote });
      toast.success(
        artifactTitle
          ? `Revoked verification: ${artifactTitle}`
          : 'Training verification revoked',
      );
      setNote('');
      setOpen(false);
      onRevoked?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message ?? '')
          : '';
      toast.error(describeError(message));
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="gap-1 text-destructive hover:text-destructive"
        disabled={disabled || revoke.isPending}
        onClick={() => setOpen(true)}
        data-testid="revoke-training-button"
      >
        <ShieldOff className="h-3 w-3" />
        Revoke
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => !revoke.isPending && setOpen(next)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Revoke Training Verification
            </DialogTitle>
            <DialogDescription>
              {artifactTitle
                ? `Are you sure you want to retract your verification of "${artifactTitle}"? `
                : 'Are you sure you want to retract this verification? '}
              The technician's completion record is preserved, but the
              manager signature will be cleared and the audit trail
              will record this revocation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label
              htmlFor="revoke-training-note"
              className="text-xs font-medium text-muted-foreground"
            >
              Reason for revocation <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="revoke-training-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Required: explain why this verification is being revoked."
              rows={3}
              disabled={revoke.isPending}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={revoke.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={!canSubmit}
            >
              {revoke.isPending ? 'Revoking…' : 'Revoke Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
